import React, { useLayoutEffect, useRef, useContext, useState, useEffect } from 'react';
import DndAnswerContext from 'components/common/Context';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import cn from '@/utils/cn';
import ScoreIcon from '@/components/common/Forms/ScoreIcon';
import ItemTypes from '../../ItemTypes';
import styles from './DraggableChoice.module.scss';
import DraggableChoiceContent from '../../DraggableChoiceContent';
import { getTextContent, resizeWithinContainer } from '../../utils';
import useDragAndDropStore from '../../useDragAndDropStore';
import useVocabularyStore from '../../useVocabularyStore';

interface DraggableChoiceType {
  isCorrect?: boolean;
  isDragged?: boolean;
  isDragDisabled?: boolean;
  isHorizontallyPositioned?: boolean;
  isAnswerKeyView: boolean;
  id: string;
  title: string;
  dragZoneWidth?: number;
  dragZoneHeight?: number;
  isRemainingChoice: boolean;
  useAnswerStyles?: boolean;
  vocabId?: string;
  showAccessibilityStyling?: boolean;
  sourceDropZoneId?: string;
}

const DraggableChoice = ({
  isAnswerKeyView,
  isCorrect: isCorrectProp = false,
  isDragDisabled = false,
  isDragged = false,
  isHorizontallyPositioned = false,
  id,
  title,
  dragZoneWidth,
  dragZoneHeight,
  isRemainingChoice,
  useAnswerStyles: useAnswerStylesProp = false,
  vocabId = null,
  showAccessibilityStyling = false,
  sourceDropZoneId = null,
}: DraggableChoiceType) => {
  const type = ItemTypes.draggableChoice;
  const label = `Draggable choice: ${title}`;
  const vocabularyTerms = useVocabularyStore(state => state.vocabularyTerms);
  const vocabLoading = useVocabularyStore(state => state.loading);
  const setIsGloballyDragging = useDragAndDropStore(state => state.setIsGloballyDragging);
  const {
    correctAnswers,
    hasReusableAnswerChoices,
    hasTransparentAnswerChoices,
    pointsPerDraggable,
    previousAnswers,
    question,
  } = useContext(DndAnswerContext);
  const [textContent, setTextContent] = useState('');
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  // Handle both data structures: reusable vs non-reusable choices
  const getPointsData = () => {
    // For non-reusable, `pointsPerDraggable` is: { 'choice_id' => [points] }
    let choicePoints = pointsPerDraggable[id] || [];

    if (hasReusableAnswerChoices && sourceDropZoneId) {
      // For reusable, `pointsPerDraggable` is: { 'choice_id' => { 'zone_id' => [points] } }
      choicePoints = pointsPerDraggable[id]?.[sourceDropZoneId] || [];
    }

    return {
      hasPoints: choicePoints.length > 0,
      latestPoints: choicePoints[choicePoints.length - 1],
    };
  };

  const { latestPoints, hasPoints } = getPointsData();

  // Check if this choice was part of any submitted attempt
  const wasPartOfSubmittedAttempt = previousAnswers.some((submittedAnswer) => {
    try {
      const parsedAnswer = typeof submittedAnswer === 'string' ? JSON.parse(submittedAnswer) : submittedAnswer;
      return parsedAnswer[sourceDropZoneId]?.includes(id);
    } catch {
      return false;
    }
  });

  // Determine correctness based on question type
  const isCorrectForCurrentPlacement = hasReusableAnswerChoices && sourceDropZoneId
    ? (correctAnswers[sourceDropZoneId]?.includes(id) && wasPartOfSubmittedAttempt && hasPoints)
    : (latestPoints === 1);

  const isCorrect = isCorrectForCurrentPlacement || isCorrectProp;
  const isPartiallyCorrect = latestPoints === 0.5;
  const useAnswerStyles = (useAnswerStylesProp || isCorrect || isPartiallyCorrect) && !isAnswerKeyView;

  useEffect(() => {
    setTextContent(getTextContent(vocabId, vocabularyTerms, vocabLoading, title, 'term'));
  }, [vocabId, vocabularyTerms, vocabLoading, title]);

  const [{ isDragging }, drag, dragPreview] = useDrag(() => ({
    canDrag: () => !isDragDisabled,
    collect: monitor => ({ isDragging: monitor.isDragging() }),
    item: { id, isDragged, sourceDropZoneId, textContent, title },
    type,
  }), [isDragDisabled, textContent, isDragged, sourceDropZoneId]);

  useEffect(() => {
    dragPreview(getEmptyImage(), { captureDraggingState: true });
  }, []);

  useEffect(() => {
    setIsGloballyDragging(isDragging);
  }, [isDragging, setIsGloballyDragging]);

  const contentRef = useRef(null);
  const resizeListener = () => {
    resizeWithinContainer(contentRef, dragZoneWidth, dragZoneHeight);
  };

  useLayoutEffect(() => {
    resizeListener();
    setTimeout(() => resizeListener(), 300);

    window.addEventListener('resize', resizeListener);

    // eslint-disable-next-line consistent-return
    return () => { window.removeEventListener('resize', resizeListener); };
  }, [
    contentRef,
    contentRef?.current,
    contentRef?.current?.clientWidth,
    contentRef?.current?.clientHeight,
    dragZoneHeight,
    dragZoneWidth,
    useAnswerStyles,
  ]);

  return (
    <div
      ref={contentRef}
      className={cn({
        [styles.draggedChoiceContainer]: isDragged,
        [styles.choicesBankItem]: isHorizontallyPositioned && !isDragged,
        [styles.textChoicesBankItem]: isHorizontallyPositioned && !textContent.includes('<img'),
      })}
    >
      {useAnswerStyles && isRemainingChoice && (
        <ScoreIcon
          isCorrect={isCorrect}
          isPartiallyCorrect={isPartiallyCorrect}
        />
      )}
      <div
        key={id}
        ref={drag}
        aria-label={label}
        className={cn(
          styles.draggableChoice, 'draggable-choice', {
            [styles.transparentBackground]: hasTransparentAnswerChoices,
            [styles.updatedQuestionScoring]: true,
            [styles.answered]: useAnswerStyles,
            [styles.correct]: isCorrect || isPartiallyCorrect,
            [styles.incorrect]: !(isCorrect || isPartiallyCorrect) && useAnswerStylesProp,
            [styles.circle]: question.data.drop_zone_shape === 'circle',
            [styles.rectangle]: question.data.drop_zone_shape !== 'circle',
            [styles.placeholder]: !isRemainingChoice,
          },
        )}
        data-draggable-id={id}
        role="button"
        tabIndex={isRemainingChoice ? 0 : -1}
        onBlurCapture={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) {
            setIsFocused(false);
          }
        }}
        onFocusCapture={() => setIsFocused(true)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <DraggableChoiceContent
          className={styles.content}
          content={textContent}
          draggableChoiceId={id}
          isAnswerKeyView={isAnswerKeyView}
          isDragged={isDragged}
          isHoveredOrFocused={isHovered || isFocused}
          showAccessibilityStyling={showAccessibilityStyling}
          sourceDropZoneId={sourceDropZoneId}
        />
      </div>
    </div>
  );
};

export default DraggableChoice;
